import type { Money } from '@alpha/api-client';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const lds = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

/**
 * 2026-09-11, LDS1 subdomain build: the platform-token label is build-time
 * configurable (VITE_TOKEN_LABEL) so the SAME source deploys as "LDS" on the
 * main site (default, unchanged) and "LDS1" on the lds1.alphaone.group build
 * — deposit/activation/team amounts everywhere formatLds() is already used.
 * Withdrawal is a SEPARATE label (VITE_WITHDRAWAL_LABEL, default = the token
 * label itself, i.e. today's behavior) because on the lds1 build withdrawals
 * pay out USDT while deposits/activation still use LDS1 — see
 * formatWithdrawalAmount() below, used only on the Withdrawal page.
 */
export const TOKEN_LABEL = (import.meta.env.VITE_TOKEN_LABEL as string | undefined) || 'LDS';
export const WITHDRAWAL_LABEL = (import.meta.env.VITE_WITHDRAWAL_LABEL as string | undefined) || TOKEN_LABEL;

/** Money arrives as a decimal string; parse only at the display boundary. */
export function formatUsd(value: Money | null | undefined): string {
  if (value == null || value === '') return '$0.00';
  const n = Number(value);
  return Number.isFinite(n) ? usd.format(n) : '$0.00';
}

export function formatLds(value: Money | null | undefined): string {
  if (value == null || value === '') return `0 ${TOKEN_LABEL}`;
  const n = Number(value);
  return Number.isFinite(n) ? `${lds.format(n)} ${TOKEN_LABEL}` : `0 ${TOKEN_LABEL}`;
}

/** Withdrawal-page-only amount formatter — see the TOKEN_LABEL docblock above. */
export function formatWithdrawalAmount(value: Money | null | undefined): string {
  if (value == null || value === '') return `0 ${WITHDRAWAL_LABEL}`;
  const n = Number(value);
  return Number.isFinite(n) ? `${lds.format(n)} ${WITHDRAWAL_LABEL}` : `0 ${WITHDRAWAL_LABEL}`;
}

export function formatNumber(value: number | null | undefined): string {
  return (value ?? 0).toLocaleString('en-US');
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${formatDate(value)} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

export type ChipTone = 'success' | 'danger' | 'warning' | 'info' | 'gold';

/**
 * Withdrawal status specifically: 'failed' (an automatic on-chain send that
 * errored, ledger debit NOT refunded) must never read like 'rejected' (debit
 * WAS refunded) — the bare word "failed" alone doesn't tell a member their
 * money is still pending a decision, not lost or already returned.
 */
export function withdrawalStatusLabel(status: string): string {
  if (status === 'failed') return 'Failed — Contact Support';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function statusTone(status: string): ChipTone {
  switch (status) {
    case 'approved':
    case 'active':
    case 'paid':
    case 'success':
    case 'closed':
      return 'success';
    case 'rejected':
    case 'blocked':
    case 'failed':
    case 'forfeited':
      return 'danger';
    case 'pending':
    case 'processing':
    case 'running':
    case 'paused':
      return 'warning';
    case 'answered':
    case 'open':
      return 'info';
    default:
      return 'gold';
  }
}
